// src/hooks/useSyncedCamera.js
import { useEffect, useRef, useCallback } from 'react';
import { useCameraSync } from '../contexts/CameraContext';

export const useSyncedCamera = (pageId, cameraRef, controlsRef) => {
  const { registerCamera, updateCameraState } = useCameraSync();
  const isInitialized = useRef(false);
  const updateTimer = useRef(null);
  
  useEffect(() => {
    if (!pageId) return;
    
    // 使用 MutationObserver 来检测相机和控制器是否准备好
    const checkCameraReady = () => {
      if (cameraRef.current && controlsRef.current && !isInitialized.current) {
        console.log(`Camera and controls ready for ${pageId}, registering...`);
        
        // 注册相机
        const cleanup = registerCamera(pageId, cameraRef.current, controlsRef.current);
        
        // 设置事件监听 - 使用防抖
        const controls = controlsRef.current;
        let debounceTimer = null;
        
        const handleChange = () => {
          if (debounceTimer) clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            updateCameraState(pageId, cameraRef.current, controlsRef.current);
          }, 16); // 约60fps
        };
        
        controls.addEventListener('change', handleChange);
        controls.addEventListener('end', handleChange);
        
        isInitialized.current = true;
        
        // 存储清理函数
        return () => {
          controls.removeEventListener('change', handleChange);
          controls.removeEventListener('end', handleChange);
          if (debounceTimer) clearTimeout(debounceTimer);
          cleanup();
          isInitialized.current = false;
        };
      }
    };
    
    // 使用 requestAnimationFrame 来定期检查，而不是 setInterval
    const checkFrame = () => {
      if (!isInitialized.current) {
        checkCameraReady();
        updateTimer.current = requestAnimationFrame(checkFrame);
      }
    };
    
    updateTimer.current = requestAnimationFrame(checkFrame);
    
    return () => {
      if (updateTimer.current) {
        cancelAnimationFrame(updateTimer.current);
      }
      isInitialized.current = false;
    };
  }, [pageId, registerCamera, updateCameraState]);
};